import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardFooter } from '../ui/card';
import { ShoppingCart, Calendar, Check, X } from 'lucide-react';
import { toast } from 'sonner';

export const ProductCard = ({ product, showRental = false }) => {
  const { t, i18n } = useTranslation();
  const { addItem } = useCart();
  const lang = i18n.language;

  const name = lang === 'fr' ? product.name_fr : product.name_en;
  const description = lang === 'fr' ? product.description_fr : product.description_en;

  const handleAddToCart = () => {
    addItem(product, 1, 'purchase');
    toast.success(t('product.addToCart'), {
      description: name
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(price);
  };

  return (
    <Card className="group bg-white border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300" data-testid={`product-card-${product.id}`}>
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <img
          src={product.images?.[0] || 'https://images.unsplash.com/photo-1612125033189-3a4950acc118'}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2">
          {product.condition === 'used' && (
            <Badge variant="secondary" className="bg-gray-100 text-gray-700 border border-gray-300">
              {t('product.used')}
            </Badge>
          )}
          {product.condition === 'new' && product.department === 'sales' && (
            <Badge variant="secondary" className="bg-black text-white">
              {t('product.new')}
            </Badge>
          )}
        </div>
        {/* Stock indicator */}
        <div className="absolute top-3 right-3">
          {product.stock_quantity > 0 ? (
            <Badge className="bg-white/90 text-black border border-gray-200">
              <Check className="h-3 w-3 mr-1" />
              {t('product.inStock')}
            </Badge>
          ) : (
            <Badge variant="destructive" className="bg-gray-100 text-gray-500">
              <X className="h-3 w-3 mr-1" />
              {t('product.outOfStock')}
            </Badge>
          )}
        </div>
      </div>

      <CardContent className="p-4">
        {/* Category */}
        <p className="text-xs text-gray-400 uppercase tracking-wider mb-1 font-mono">
          {product.category}
        </p>
        
        {/* Title */}
        <h3 className="font-semibold text-black text-lg mb-2 line-clamp-2">
          {name}
        </h3>
        
        {/* Description */}
        {description && (
          <p className="text-gray-500 text-sm line-clamp-2 mb-3">
            {description}
          </p>
        )}

        {/* Pricing */}
        <div className="space-y-1">
          {showRental && product.rental_price_daily ? (
            <>
              <p className="text-2xl font-bold text-black font-mono">
                {formatPrice(product.rental_price_daily)}
                <span className="text-sm text-gray-500 font-normal">{t('product.perDay')}</span>
              </p>
              {product.rental_price_weekly && (
                <p className="text-sm text-gray-500 font-mono">
                  {formatPrice(product.rental_price_weekly)}{t('product.perWeek')} • {formatPrice(product.rental_price_monthly)}{t('product.perMonth')}
                </p>
              )}
            </>
          ) : (
            <p className="text-2xl font-bold text-black font-mono">
              {formatPrice(product.price)}
              {product.department === 'software' && (
                <span className="text-sm text-gray-500 font-normal">{t('product.perMonth')}</span>
              )}
            </p>
          )}
        </div>
      </CardContent>

      <CardFooter className="p-4 pt-0 flex gap-2">
        {showRental ? (
          <Link to={`/rental/${product.id}`} className="flex-1">
            <Button className="w-full bg-black text-white hover:bg-gray-800" data-testid={`rent-btn-${product.id}`}>
              <Calendar className="h-4 w-4 mr-2" />
              {t('product.rent')}
            </Button>
          </Link>
        ) : product.department === 'software' ? (
          <Link to={`/software/${product.id}`} className="flex-1">
            <Button className="w-full bg-black text-white hover:bg-gray-800" data-testid={`subscribe-btn-${product.id}`}>
              {t('product.subscribe')}
            </Button>
          </Link>
        ) : (
          <Button 
            className="flex-1 bg-black text-white hover:bg-gray-800" 
            onClick={handleAddToCart}
            disabled={product.stock_quantity === 0}
            data-testid={`add-cart-btn-${product.id}`}
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            {t('product.addToCart')}
          </Button>
        )}
        <Link to={`/${product.department}/${product.id}`}>
          <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50" data-testid={`view-btn-${product.id}`}>
            {t('common.view')}
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
};
